import React from 'react'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { SentenceRetrievalResult } from 'schemas/SentenceRetrievalResultSchema'
import StyledAvatar from 'components/visualization/SoundbiteFrame/StyledAvatar'
import HeaderInfo from 'components/visualization/SoundbiteFrame/HeaderInfo'
import LinkedDate from 'components/visualization/SoundbiteFrame/LinkedDate'
import LinkedDomainIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedDomainIcon'
import PostContent from 'components/visualization/SoundbiteFrame/PostContent/PostContent'
import { isEmpty } from 'lodash'
import { createStreamWidgetDataFromSentenceRetrieval } from 'components/widget/StreamWidgets/share/Stream/createStreamWidgetDataFromSentenceRetrieval'
import { usePageConfigUser } from 'hooks/usePageConfig'
import LinkedVocIcon from 'components/visualization/SoundbiteFrame/DomainIcon/LinkedVocIcon'
import Divider from 'components/visualization/SoundbiteFrame/Divider'
import InternalContents from 'components/visualization/SoundbiteFrame/InternalContents/InternalContents'
import { StreamData } from 'types/streamData'
import ActionIcons from 'components/visualization/SoundbiteFrame/ActionIcons'
import { StreamSymbol } from 'enums'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import CompactSoundbiteNoHeaderFrame from 'components/visualization/SoundbiteFrame/CompactFrame/CompactSoundbiteNoHeaderFrame'
import CompactSoundbiteDefaultFrame from 'components/visualization/SoundbiteFrame/CompactFrame/CompactSoundbiteDefaultFrame'
import CompactVocSoundbiteNoHeaderFrame from 'components/visualization/SoundbiteFrame/CompactFrame/CompactVocSoundbiteNoHeaderFrame'
import CompactVocSoundbiteFrame from 'components/visualization/SoundbiteFrame/CompactFrame/CompactVocSoundbiteFrame'
import CompactPhotoUserTagContent from 'components/visualization/SoundbiteFrame/CompactFrame/CompactPhotoUserTagContent'
import styled from 'styled-components'
import ViewOnIcon from 'components/visualization/SoundbiteFrame/ActionIcons/ViewOnIcon'
import Deleted from './Deleted'

interface SentenceSoundbiteProps {
  data: SentenceRetrievalResult
  /**
   * for test id
   */
  serialNumber: number
}

const StyledContainer = styled.div`
  margin: 0 -16px;
`

interface VocSoundbiteFrameProps {
  hasHeader: boolean
  soundbite: StreamData
  index: number
  theme: Theme
}

const VocSoundbiteFrame = ({ hasHeader, soundbite, index, theme }: VocSoundbiteFrameProps) => {
  const hasActionItems = !!(soundbite.sourceType && soundbite.url)
  if (hasHeader) {
    return (
      <CompactVocSoundbiteFrame
        avatar={<StyledAvatar streamData={soundbite} diameter="32px" fallbackFontSize="16px" />}
        dateItem={<LinkedDate streamData={soundbite} />}
        domainIcon={<LinkedVocIcon streamData={soundbite} />}
        headerInfo={<HeaderInfo streamData={soundbite} />}
        internalContents={
          <>
            {hasHeader && <Divider />}
            <InternalContents streamData={soundbite} highlighting={false} />
            {hasActionItems && <Divider />}
          </>
        }
        actionItems={
          hasActionItems && (
            <>
              {soundbite.sourceType && soundbite.url && (
                <ViewOnIcon
                  source={soundbite.sourceType!}
                  domain={soundbite.domain!}
                  url={soundbite.url}
                  displayText={false}
                  iconColor={sys[theme].interaction.primary.default}
                />
              )}
            </>
          )
        }
        serialNumber={index}
      />
    )
  }
  return (
    <CompactVocSoundbiteNoHeaderFrame
      avatar={<StyledAvatar streamData={soundbite} diameter="32px" fallbackFontSize="16px" />}
      dateItem={<LinkedDate streamData={soundbite} />}
      domainIcon={<LinkedVocIcon streamData={soundbite} />}
      internalContents={
        <>
          <InternalContents streamData={soundbite} highlighting={false} />
          {hasActionItems && <Divider />}{' '}
        </>
      }
      actionItems={
        hasActionItems && (
          <>
            {soundbite.sourceType && soundbite.url && (
              <ViewOnIcon
                source={soundbite.sourceType!}
                domain={soundbite.domain!}
                url={soundbite.url}
                displayText={false}
                iconColor={sys[theme].interaction.primary.default}
              />
            )}
          </>
        )
      }
      serialNumber={index}
    />
  )
}
interface CompactSoundbiteProps {
  soundbite: StreamData
  index: number
  hasHeader: boolean
  theme: Theme
}
const CompactSoundbite = ({ soundbite, index, hasHeader, theme }: CompactSoundbiteProps) => {
  if (!hasHeader) {
    return (
      <CompactSoundbiteNoHeaderFrame
        avatar={<StyledAvatar streamData={soundbite} diameter="32px" fallbackFontSize="16px" />}
        dateItem={<LinkedDate streamData={soundbite} />}
        domainIcon={<LinkedDomainIcon streamData={soundbite} />}
        postContent={<PostContent streamData={soundbite} highlighting={false} />}
        actionItems={
          <ActionIcons
            isCompact
            streamData={soundbite}
            displayText={false}
            iconColor={sys[theme].interaction.primary.default}
          />
        }
        photoUserTagContent={
          !isEmpty(soundbite.photoUserTags) && <CompactPhotoUserTagContent streamData={soundbite} />
        }
        serialNumber={index}
      />
    )
  }

  return (
    <CompactSoundbiteDefaultFrame
      replySection={{
        display: !isEmpty(soundbite.replyToAuthors),
        author: soundbite.replyToAuthors
      }}
      retweetSection={{
        display: soundbite.isRetweet! && soundbite.isTweet!,
        author: soundbite.authorName,
        url: soundbite.authorUrl,
        isVerified: soundbite.authorVerified
      }}
      reblogSection={{
        display: soundbite.isRetweet! && !soundbite.isTweet!,
        author: soundbite.authorName,
        url: soundbite.authorUrl
      }}
      avatar={<StyledAvatar streamData={soundbite} diameter="32px" fallbackFontSize="16px" />}
      dateItem={<LinkedDate streamData={soundbite} />}
      domainIcon={<LinkedDomainIcon streamData={soundbite} />}
      headerInfo={<HeaderInfo streamData={soundbite} fontSize={14} />}
      postContent={<PostContent streamData={soundbite} highlighting={false} />}
      actionItems={
        <ActionIcons
          streamData={soundbite}
          displayText={false}
          iconColor={sys[theme].interaction.primary.default}
          isCompact
        />
      }
      photoUserTagContent={
        !isEmpty(soundbite.photoUserTags) && <CompactPhotoUserTagContent streamData={soundbite} />
      }
      serialNumber={index}
    />
  )
}

const SentenceSoundbite = ({ data, serialNumber }: SentenceSoundbiteProps) => {
  const { timeZone } = usePageConfigUser()
  const theme = useDisplayTheme() as Theme

  const {
    soundbites,
    deletedSentenceCount,
    deletedSentenceDateRangeFrom,
    deletedSentenceDateRangeTo
  } = createStreamWidgetDataFromSentenceRetrieval(data, timeZone)

  const unDeletedSoundbites = soundbites.filter((soundbite) => !soundbite.deleted)
  const unDeletedSoundbitesLength = unDeletedSoundbites.length

  return (
    <StyledContainer>
      {unDeletedSoundbites.map((soundbite, index) => {
        const hasHeader = !!soundbite.title || !!soundbite.authorName || !!soundbite.authorHandle
        if (soundbite.dataStream === StreamSymbol.VOC) {
          return (
            <VocSoundbiteFrame
              // eslint-disable-next-line react/no-array-index-key
              key={`${serialNumber}-${index}`}
              soundbite={soundbite}
              index={index}
              hasHeader={hasHeader}
              theme={theme}
            />
          )
        }
        return (
          <CompactSoundbite
            // eslint-disable-next-line react/no-array-index-key
            key={`${serialNumber}-${index}`}
            soundbite={soundbite}
            index={index}
            hasHeader={hasHeader}
            theme={theme}
          />
        )
      })}
      <Deleted
        deletedSentenceCount={deletedSentenceCount}
        deletedSentenceDateRangeFrom={deletedSentenceDateRangeFrom!}
        deletedSentenceDateRangeTo={deletedSentenceDateRangeTo!}
        serialNumber={unDeletedSoundbitesLength}
      />
    </StyledContainer>
  )
}
export default SentenceSoundbite
